import React, {useEffect} from 'react'
import Colors from "../../components/ui/Colors";
import { css } from "@emotion/react";
import {Grid, Drawer, List, ListItem, ListItemButton, ListItemText, Typography,
    Box, Container, Card, CardContent, CardActionArea, ListItemIcon} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import { useTranslation } from 'react-i18next';
import {setUxLanguage, setUserLanguage} from "../../utilityFunctions/AuthUtil";
import swal from "sweetalert";
import CustomLoader from "../../components/CustomLoader";
import { useHistory } from "react-router-dom";
import styled from '@emotion/styled';
import { hasPermission, hasAnyPermission, isRoot, permissionsLoaded } from "../../components/AccessControl";
import { useSidebar } from "../../layout/SidebarContext";
import { federationEnabled } from "../../Api";
import {
    SIDEBAR_WIDTH,
    MOBILE_SIDEBAR_WIDTH,
    CONTENT_TOP_OFFSET,
} from "../../layout/layoutMetrics";

// Menu Icons
import HomeIcon from '@mui/icons-material/Home';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import MapIcon from '@mui/icons-material/Map';
import PeopleIcon from '@mui/icons-material/People';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SettingsIcon from '@mui/icons-material/Settings';
import ChatIcon from '@mui/icons-material/Chat';
import ForumIcon from '@mui/icons-material/Forum';
import StorageIcon from '@mui/icons-material/Storage';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import HelpIcon from '@mui/icons-material/Help';
import BugReportIcon from '@mui/icons-material/BugReport';
import TableChartIcon from '@mui/icons-material/TableChart';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DashboardIcon from '@mui/icons-material/Dashboard';

// Tenant-admin is a MEMBERSHIP fact — derived from the effective permission set,
// never from the legacy direct `loginUser.roles[0]`. Holding any ADMIN-tier tenant
// permission (the set the V3 seed grants the ADMIN role) selects the admin shell.
const ADMIN_TIER_PERMISSIONS = [
    "USER_MANAGE", "ROLE_MANAGE", "TENANT_METADATA_MANAGE",
    "TENANT_SETTINGS_MANAGE", "CLIENT_PII_MANAGE", "TABLE_INJECT",
];
const isTenantAdmin = (loginUser) => hasAnyPermission(loginUser, ADMIN_TIER_PERMISSIONS);

// Visibility check for the tenant-data sections (Analytics & Reports, Geographic
// Data, AI Tools). These are baseline capabilities the ADMIN role holds in full
// (V3 seed: ADMIN = USER baseline + tenant administration), so they must render
// for admins too — they used to be trapped inside the USER-only menu branch.
// Lenient like AccessControl.isAuthorized: when no permission set has resolved
// (degraded bootstrap / pre-P8 token) we still show the entry; the server re-checks.
const canSee = (loginUser, permissions) => {
    if (!permissions || permissions.length === 0) return true;
    if (!permissionsLoaded(loginUser)) return true;
    return hasAnyPermission(loginUser, permissions);
};

const cssLoader = css`
  display: block;
  margin-left: auto;
  margin-right: auto;
`;

// Styled Components
const RoleHeader = styled(Typography)(({ theme }) => ({
    color: Colors.primary,
    padding: '16px 16px 8px 16px',
    fontWeight: 700,
    fontSize: '1rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: `2px solid ${Colors.primary}`,
    marginBottom: '16px'
}));

const CategoryHeader = styled(Typography)(({ theme }) => ({
    color: Colors.primary,
    padding: '16px 16px 8px 16px',
    fontWeight: 600,
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px'
}));

const MenuItemButton = styled(ListItemButton)(({ theme }) => ({
    padding: '8px 16px 8px 32px',
    '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    '& .MuiListItemIcon-root': {
        minWidth: '40px',
        color: Colors.primary
    }
}));

const HeroSection = styled(Box)(({ theme }) => ({
    background: `linear-gradient(135deg, ${Colors.primary}15 0%, ${Colors.primary}05 100%)`,
    padding: '32px 16px',
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
    borderRadius: '16px',
    marginBottom: '24px'
}));

const ActionCard = styled(Card)(({ theme }) => ({
    height: '100%',
    borderRadius: '24px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.2s, box-shadow 0.2s',
    backgroundColor: 'background.paper',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 12px rgba(0, 0, 0, 0.15)',
        backgroundColor: `${Colors.primary}05`
    }
}));

const CardIcon = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '16px',
    '& svg': {
        fontSize: '3rem',
        color: Colors.primary
    }
}));

const LogoImage = styled('img')(({ theme }) => ({
    height: 'auto',
    width: '200px',
    marginBottom: '16px'
}));
  
let openLlmSessions=false;
let openManageClients=false;
  
/**
 * Double duty component:
 *   - `asMenu` (rendered once by AuthedRoutes): renders ONLY the navigation
 *     drawers — permanent at md and up, temporary below, driven by the navbar
 *     hamburger through SidebarContext.
 *   - default (the /handle_profile route): renders the dashboard page only.
 *
 * They used to be rendered together, so every route mounted a full hidden copy
 * of the dashboard inside the sidebar slot, and /handle_profile mounted the
 * drawers twice.
 */
export default function HanldeProfile(props) {
    const {loginUser, asMenu = false} = props
    const {mobileOpen, toggleSidebar, closeSidebar} = useSidebar();

    let history = useHistory();

    const [loading, setLoading] = React.useState(false);

    const { t, i18n } = useTranslation();

    const [mainRole, setMainRole] = React.useState(null);

    useEffect(() => {

        handleAdmin();
        // Rebuild when the tenant (and thus the permission set) changes.
    }, [loginUser.activeTenantId, loginUser.permissions]);

    // Navigating from the temporary drawer should dismiss it.
    useEffect(() => {
        if (!asMenu) return undefined;
        return history.listen(() => closeSidebar());
    }, [asMenu, history, closeSidebar]);

    const handleLanguageChange = async (selectedLanguage) => {
        i18n.changeLanguage(selectedLanguage);
        
        const UserLanguage={
            language: selectedLanguage
        }
        const langResponse = await setUxLanguage(UserLanguage, loginUser.jwtToken);
    };

    const handleUserLanguageChange = async (selectedLanguage) => {
        i18n.changeLanguage(selectedLanguage);

        const UserLanguage={
            language: selectedLanguage
        }
        const langResponse = await setUserLanguage(UserLanguage, loginUser.jwtToken);

    };


    const handleAdmin = () => {

        if(loginUser.roles && !(Object.keys(loginUser.roles).length === 0 && loginUser.roles.constructor === Object)){
            
            profileOptionsDrawer(loginUser.roles[0]);
            setMainRole(loginUser.roles[0].authority);
        }

    };
    
    const[profileOptions, setProfileOptions] = React.useState("");

    const profileOptionsDrawer = (userAuth) => {
        const userProfile = userAuth.authority;
        console.log("userProfile: ", userProfile);

        // Tenant-data sections, shared by the USER and ADMIN shells. Each item
        // declares the permissions that make it meaningful, so the same
        // definition serves any role that holds them.
        const menuCategories = {
            userAnalytics: {
                title: "Analytics & Reports",
                icon: <AnalyticsIcon />,
                items: [
                    { text: "Reports", icon: <AssessmentIcon />, action: goToDataReports,
                      perms: ["REPORT_CREATE", "REPORT_VIEW_OWN"] },
                    { text: "Analytics", icon: <AnalyticsIcon />, action: goToDataAnalytics,
                      perms: ["METRICS_VIEW", "SQL_QUERY_RUN"] },
                    { text: "Client Metrics", icon: <PeopleIcon />, action: goToClientMetrics,
                      perms: ["METRICS_VIEW", "CLIENT_DASHBOARD_VIEW"] },
                    // Hidden unless the REACT_APP_FEDERATION_ENABLED build flag is "true".
                    ...(federationEnabled
                        ? [{ text: t("Federation.navTitle"), icon: <CloudSyncIcon />, action: goToFederation,
                             perms: ["FEDERATION_MANAGE"] }]
                        : []),
                ]
            },
            userMaps: {
                title: "Geographic Data",
                icon: <MapIcon />,
                items: [
                    { text: "Census Data", icon: <MapIcon />, action: goToCensus,
                      perms: ["DATA_REFERENCE_VIEW"] },
                    { text: "Food Insecurity", icon: <RestaurantIcon />, action: goToCensusTracts,
                      perms: ["DATA_REFERENCE_VIEW"] },
                ]
            },
            userAI: {
                title: "AI Tools",
                icon: <SmartToyIcon />,
                items: [
                    { text: "Data Love AI", icon: <ChatIcon />, action: goToAskDorothy,
                      perms: ["CHAT_USE"] },
                    { text: "AI Reports Agent", icon: <AutoAwesomeIcon />, action: goToReportAgent,
                      perms: ["REPORT_CREATE"] },
                    { text: t("Dashboards.navTitle"), icon: <DashboardIcon />, action: goToDashboards,
                      perms: ["DASHBOARD_VIEW"] },
                ]
            },
        };

        // A category with no visible items renders nothing at all — no orphan header.
        const renderMenuSection = (category) => {
            const items = category.items.filter((item) => canSee(loginUser, item.perms));
            if (items.length === 0) return null;
            return (
                <React.Fragment key={category.title}>
                    <CategoryHeader>
                        {category.icon}
                        {category.title}
                    </CategoryHeader>
                    {items.map((item) => (
                        <MenuItemButton
                            key={item.text}
                            onClick={item.action}
                        >
                            <ListItemIcon>
                                {item.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.text}
                                primaryTypographyProps={{
                                    variant: "body2",
                                    color: "text.primary"
                                }}
                            />
                        </MenuItemButton>
                    ))}
                </React.Fragment>
            );
        };

        const dataSections = Object.values(menuCategories).map(renderMenuSection);

        const getRoleTitle = () => {
            if (isRoot(loginUser)) return "PLATFORM ROOT";
            if (isTenantAdmin(loginUser)) return "ADMIN DASHBOARD";
            return "USER DASHBOARD";
        };

        // Phase 8 — administration & access (permission-driven). Lifted out of the
        // ADMIN string branch so it renders for ANY user who holds the authority
        // (its inner permission guards do the gating). Emitted exactly once per
        // branch below, so an ADMIN/ROOT never sees a duplicate.
        const platformAdminSection = (
            <>
                {(hasAnyPermission(loginUser, ["ROLE_MANAGE", "USER_MANAGE", "PLATFORM_ROLE_MANAGE", "PLATFORM_USER_MANAGE"])
                    || hasAnyPermission(loginUser, ["TENANT_METADATA_MANAGE", "PLATFORM_PROMPT_VIEW", "TABLE_INJECT", "REPORT_TRACE_VIEW"])
                    || (isRoot(loginUser) && hasPermission(loginUser, "PLATFORM_DATASET_GRANT"))) && (
                    <CategoryHeader><AdminPanelSettingsIcon />Administration</CategoryHeader>
                )}
                {hasAnyPermission(loginUser, ["ROLE_MANAGE", "USER_MANAGE", "PLATFORM_ROLE_MANAGE", "PLATFORM_USER_MANAGE"]) && (
                    <MenuItemButton onClick={goToAccessControl}>
                        <ListItemIcon><AdminPanelSettingsIcon /></ListItemIcon>
                        <ListItemText primary="Access Control" primaryTypographyProps={{variant: "body2"}}/>
                    </MenuItemButton>
                )}
                {/* Cross-tenant "all users" view — ROOT only (PLATFORM_USER_MANAGE). */}
                {hasPermission(loginUser, "PLATFORM_USER_MANAGE") && (
                    <MenuItemButton onClick={goToPlatformUsers}>
                        <ListItemIcon><PeopleIcon /></ListItemIcon>
                        <ListItemText primary="All Users" primaryTypographyProps={{variant: "body2"}}/>
                    </MenuItemButton>
                )}
                {hasPermission(loginUser, "TENANT_METADATA_MANAGE") && (
                    <MenuItemButton onClick={goToTenantMetadata}>
                        <ListItemIcon><TableChartIcon /></ListItemIcon>
                        <ListItemText primary="Tenant Metadata" primaryTypographyProps={{variant: "body2"}}/>
                    </MenuItemButton>
                )}
                {isRoot(loginUser) && hasPermission(loginUser, "PLATFORM_DATASET_GRANT") && (
                    <MenuItemButton onClick={goToSharedDatasets}>
                        <ListItemIcon><StorageIcon /></ListItemIcon>
                        <ListItemText primary="Shared Datasets" primaryTypographyProps={{variant: "body2"}}/>
                    </MenuItemButton>
                )}
                {/* System prompts is a PLATFORM (ROOT) capability — backend gates it on
                    PLATFORM_PROMPT_VIEW/EDIT — so it is surfaced here, not in the ADMIN branch. */}
                {hasPermission(loginUser, "PLATFORM_PROMPT_VIEW") && (
                    <MenuItemButton onClick={gotoManagePrompts}>
                        <ListItemIcon><SettingsIcon /></ListItemIcon>
                        <ListItemText primary={t("HandleProfile.systemPrompts")} primaryTypographyProps={{variant: "body2"}}/>
                    </MenuItemButton>
                )}
                {/* Permission-driven so ADMIN sees them regardless of role ordering, and
                    ROOT sees Report Traces once granted REPORT_TRACE_VIEW. */}
                {hasPermission(loginUser, "TABLE_INJECT") && (
                    <MenuItemButton onClick={goToInjectTable}>
                        <ListItemIcon><CloudUploadIcon /></ListItemIcon>
                        <ListItemText primary="Inject Table" primaryTypographyProps={{variant: "body2"}}/>
                    </MenuItemButton>
                )}
                {hasPermission(loginUser, "REPORT_TRACE_VIEW") && (
                    <MenuItemButton onClick={goToReportTraces}>
                        <ListItemIcon><BugReportIcon /></ListItemIcon>
                        <ListItemText primary="Report Traces" primaryTypographyProps={{variant: "body2"}}/>
                    </MenuItemButton>
                )}
            </>
        );

        // ROOT is checked first (via the authoritative isRoot helper) so a platform
        // root gets its menu regardless of role ordering in loginUser.roles.
        if (isRoot(loginUser)) {
            setProfileOptions(
                <List sx={{width: "100%"}}>
                    <RoleHeader>{getRoleTitle()}</RoleHeader>
                    <MenuItemButton onClick={goToDataHome}>
                        <ListItemIcon>
                            <HomeIcon />
                        </ListItemIcon>
                        <ListItemText primary="Home" primaryTypographyProps={{variant: "body1", fontWeight: 600}}/>
                    </MenuItemButton>
                    <MenuItemButton onClick={goToAskDorothy}>
                        <ListItemIcon>
                            <ChatIcon />
                        </ListItemIcon>
                        <ListItemText primary="Data Love AI" primaryTypographyProps={{variant: "body1", fontWeight: 600}}/>
                    </MenuItemButton>
                    {platformAdminSection}
                </List>
            );
        } else if (!isTenantAdmin(loginUser)) {
            setProfileOptions(
                <List sx={{width: "100%"}}>
                    <RoleHeader>{getRoleTitle()}</RoleHeader>
                    {dataSections}
                    {platformAdminSection}
                </List>
            );
        } else {
            setProfileOptions(
                <List sx={{width: "100%"}}>
                    <RoleHeader>{getRoleTitle()}</RoleHeader>
                    {/* An ADMIN holds the whole USER baseline (V3 seed), so it gets the
                        same Analytics & Reports / Geographic Data / AI Tools sections.
                        "Data Love AI" now lives inside AI Tools — no standalone entry
                        here, otherwise it would render twice. */}
                    {dataSections}
                    <ListItem disablePadding>
                        <ListItemButton onClick={handleExpandMangeClients}>
                            <Grid container direction="column">
                                <Grid item xs={12} sm={12} md={12} lg={12} sx={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                                    <ListItemIcon>
                                        <ManageAccountsIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={t("HandleProfile.manageClients")} primaryTypographyProps={{variant: "body1", fontWeight: 600}}/>
                                    {openManageClients ? <ExpandLess /> : <ExpandMore />}
                                </Grid>
                                <Grid item xs={12} sm={12} md={12} lg={12}>
                                    <Collapse in={openManageClients} timeout="auto">
                                        <MenuItemButton onClick={gotoManageClients}>
                                            <ListItemIcon>
                                                <PeopleIcon />
                                            </ListItemIcon>
                                            <ListItemText primary={t("HandleProfile.manageClients")} primaryTypographyProps={{variant: "body2"}}/>
                                        </MenuItemButton>
                                        <MenuItemButton onClick={goToConsumers}>
                                            <ListItemIcon>
                                                <AssessmentIcon />
                                            </ListItemIcon>
                                            <ListItemText primary={t("HandleProfile.consumers")} primaryTypographyProps={{variant: "body2"}}/>
                                        </MenuItemButton>
                                    </Collapse>
                                </Grid>
                            </Grid>
                        </ListItemButton>
                    </ListItem>
                    {/* System Prompts, Inject Table and Report Traces are now rendered
                        permission-driven inside platformAdminSection (below), so they no
                        longer depend on roles[0] resolving to ADMIN. */}

                    {/* Phase 8 — administration & access (permission-driven) */}
                    {platformAdminSection}
                </List>
            );
        }
    };

    const handleExpandSessions = () => {
        openLlmSessions=!openLlmSessions;
        handleAdmin();
    };

    const handleExpandMangeClients = () => {
        openManageClients=!openManageClients;
        handleAdmin();
    };

    const goToReportTraces = () => {
        history.push({pathname: "/admin/report_traces"});
    };

    const goToInjectTable = () => {
        history.push({pathname: "/admin/inject_table"});
    };

    const goToAccessControl = () => {
        history.push({pathname: "/admin/access_control"});
    };

    const goToPlatformUsers = () => {
        history.push({pathname: "/admin/platform_users"});
    };

    const goToTenantMetadata = () => {
        history.push({pathname: "/admin/tenant_metadata"});
    };

    const goToSharedDatasets = () => {
        history.push({pathname: "/admin/shared_datasets"});
    };

    const goToDataHome = () =>{
        history.push({
            pathname: "/data_analytics/home",
        })
    };

    const goToDataAnalytics = () =>{
        history.push({
            pathname: "/data_analytics/analytics",
        })
    };

    const goToClientMetrics = () =>{
        history.push({
            pathname: "/data_analytics/client_metrics",
        })
    };

    const goToCensus = () => {
        history.push({
            pathname: "/data_analytics/census",
        })
    };

    const goToCensusTracts = () => {
        history.push({
            pathname: "/data_analytics/census_tracts",
        })
    };

    const goToAskDorothy = () =>{
        history.push({
            pathname: "/data_analytics/nlq_chat",
        })
    };

    const goToReportAgent = () =>{
        history.push({
            pathname: "/data_analytics/llm_report_agent",
        })
    };

    const goToFederation = () =>{
        history.push({
            pathname: "/data_analytics/federation",
        })
    };

    const goToDashboards = () =>{
        history.push({
            pathname: "/data_analytics/dashboards",
        })
    };

    const goToDataReports = () =>{
        history.push({
            pathname: "/data_analytics/reports",
        })
    };

    const goToConsumers = () =>{
        history.push({pathname: "/admin/reports_consumers"});
    };

    const gotoManagePrompts = () =>{
        history.push({pathname: "/admin/llm_system_prompts"});
    };

    const gotoSessionsList = () =>{
        history.push({pathname: "/admin/llm_sessions"});
    };

    const gotoManageClients = () =>{
        history.push({pathname: "/admin/manage_users"});
    };

    const goToCommunityForums = () =>{
        swal({
            title: t("HandleProfile.comingSoon"),
            icon: "info",
            button: "OK",
        });
        return;
    };

    const goToDataHub = () =>{
        swal({
            title: t("HandleProfile.comingSoon"),
            icon: "info",
            button: "OK",
        });
        return;
    };

    const goToSettings = () =>{
        swal({
            title: t("HandleProfile.comingSoon"),
            icon: "info",
            button: "OK",
        });
        return;
    };

    const goToHelp = () =>{
        swal({
            title: t("HandleProfile.comingSoon"),
            icon: "info",
            button: "OK",
        });
        return;
    };

    const getRoleSpecificActions = () => {
        if (!loginUser) return [];

        // Tier is a MEMBERSHIP fact (scope/permissions), not the legacy direct role.
        const isRootUser = isRoot(loginUser);
        const tenantAdmin = isTenantAdmin(loginUser);

        if (isRootUser) {
            return [
                {
                    title: "Access Control",
                    description: "Manage roles, users, and platform access",
                    icon: <AdminPanelSettingsIcon />,
                    action: goToAccessControl
                },
                {
                    title: "Tenant Metadata",
                    description: "Curate tenant metadata used by the AI pipelines",
                    icon: <TableChartIcon />,
                    action: goToTenantMetadata
                },
                {
                    title: "Shared Datasets",
                    description: "Grant and manage cross-tenant shared datasets",
                    icon: <StorageIcon />,
                    action: goToSharedDatasets
                },
                {
                    title: "Data Love AI",
                    description: "Ask questions in natural language and get grounded answers",
                    icon: <ChatIcon />,
                    action: goToAskDorothy
                }
            ];
        } else if (!tenantAdmin) {
            return [
                {
                    title: "Data Analytics",
                    description: "Explore comprehensive analytics and insights",
                    icon: <AnalyticsIcon />,
                    action: goToDataAnalytics
                },
                {
                    title: "Census Data",
                    description: "Explore geographic census information",
                    icon: <MapIcon />,
                    action: goToCensus
                },
                {
                    title: "AI Reports Agent",
                    description: "Generate automated AI-powered reports",
                    icon: <AutoAwesomeIcon />,
                    action: goToReportAgent
                }
            ];
        } else {
            // ADMIN quick actions: the USER-baseline shortcuts (an ADMIN holds those
            // permissions too) followed by the tenant-administration ones, each shown
            // only when the permission backing it is actually held.
            return [
                {
                    title: "Data Love AI",
                    description: "Ask questions in natural language and get grounded answers",
                    icon: <ChatIcon />,
                    action: goToAskDorothy,
                    perms: ["CHAT_USE"]
                },
                {
                    title: "Data Analytics",
                    description: "Explore comprehensive analytics and insights",
                    icon: <AnalyticsIcon />,
                    action: goToDataAnalytics,
                    perms: ["METRICS_VIEW", "SQL_QUERY_RUN"]
                },
                {
                    title: "Census Data",
                    description: "Explore geographic census information",
                    icon: <MapIcon />,
                    action: goToCensus,
                    perms: ["DATA_REFERENCE_VIEW"]
                },
                {
                    title: "AI Reports Agent",
                    description: "Generate automated AI-powered reports",
                    icon: <AutoAwesomeIcon />,
                    action: goToReportAgent,
                    perms: ["REPORT_CREATE"]
                },
                {
                    title: "System Prompts",
                    description: "Configure LLM system prompts",
                    icon: <SettingsIcon />,
                    action: gotoManagePrompts,
                    perms: ["PLATFORM_PROMPT_VIEW"]
                },
                {
                    title: "Pipeline Traces",
                    description: "Inspect AI report pipeline step-by-step execution",
                    icon: <BugReportIcon />,
                    action: goToReportTraces,
                    perms: ["REPORT_TRACE_VIEW"]
                }
            ].filter((card) => canSee(loginUser, card.perms));
        }

        return [];
    };

    // Shared by both drawers so the menu is defined exactly once.
    const menuContent = (
        <Grid container>
            <Grid item xs={12}>
                {profileOptions}
            </Grid>
            {mainRole && mainRole==="USER" &&
                <Grid item xs={12} sx={{display: "flex", justifyContent: "center", marginTop: "2rem"}}>
                    <List sx={{width: "100%"}}>
                        <ListItem disablePadding>
                            <ListItemButton onClick={goToCommunityForums} sx={{justifyContent: "center"}}>
                                <ListItemIcon>
                                    <ForumIcon />
                                </ListItemIcon>
                                <ListItemText
                                    primary={t("HandleProfile.communityForums")}
                                    primaryTypographyProps={{
                                        variant: "body2",
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                        <ListItem disablePadding>
                            <ListItemButton onClick={goToDataHub} sx={{justifyContent: "center"}}>
                                <ListItemIcon>
                                    <StorageIcon />
                                </ListItemIcon>
                                <ListItemText
                                    primary={t("HandleProfile.dataHub")}
                                    primaryTypographyProps={{
                                        variant: "body2",
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                        <ListItem disablePadding>
                            <ListItemButton onClick={goToSettings} sx={{justifyContent: "center"}}>
                                <ListItemIcon>
                                    <SettingsIcon />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Settings"
                                    primaryTypographyProps={{
                                        variant: "body2",
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                        <ListItem disablePadding>
                            <ListItemButton onClick={goToHelp} sx={{justifyContent: "center"}}>
                                <ListItemIcon>
                                    <HelpIcon />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Help"
                                    primaryTypographyProps={{
                                        variant: "body2",
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                    </List>
                </Grid>
            }
        </Grid>
    );

    // Sidebar mode — drawers only, no page content.
    if (asMenu) {
        return (
            <React.Fragment>
                {/* Temporary drawer, below md. Opened from the navbar hamburger. */}
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={toggleSidebar}
                    ModalProps={{
                        keepMounted: true,
                    }}
                    sx={{
                        display: { xs: 'block', md: "none" },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: MOBILE_SIDEBAR_WIDTH,
                            maxWidth: '100%',
                            marginTop: CONTENT_TOP_OFFSET,
                            height: `calc(100% - ${CONTENT_TOP_OFFSET})`,
                            backgroundColor: 'background.paper',
                            boxShadow: 3,
                            overflowY: 'auto'
                        }
                    }}
                >
                    {menuContent}
                </Drawer>

                {/* Permanent drawer, md and up. The paper is `position: fixed`, so
                    the docked root deliberately reserves NO width in flow — the
                    content column offsets itself by SIDEBAR_WIDTH instead. Giving
                    the root a width here would double the offset. */}
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', md: 'flex'},
                        width: 0,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: `${SIDEBAR_WIDTH}px`,
                            marginTop: CONTENT_TOP_OFFSET,
                            height: `calc(100vh - ${CONTENT_TOP_OFFSET})`,
                            overflowY: 'auto',
                            backgroundColor: 'background.paper',
                            boxShadow: 2,
                            border: 'none',
                            position: 'fixed',
                            left: 0
                        }
                    }}
                    open
                >
                    {menuContent}
                </Drawer>
            </React.Fragment>
        );
    }

  return (
      <React.Fragment>
        {loading ? <CustomLoader /> :
            <Box sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}>
                {/* Main Content Area */}
                <Box sx={{
                    width: '100%',
                    minHeight: `calc(100vh - ${CONTENT_TOP_OFFSET})`,
                    pt: CONTENT_TOP_OFFSET
                }}>
                        <Box sx={{
                            bgcolor: 'background.default',
                            minHeight: '100%',
                            py: 3,
                            px: { xs: 1, sm: 2 },
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            <Container maxWidth="lg" sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                {/* Mobile Logo and Title */}
                                <Box sx={{ 
                                    display: { xs: 'block', sm: 'none' },
                                    textAlign: 'center',
                                    py: 3
                                }}>
                                    <LogoImage 
                                        src="/images/logo_datalove.png" 
                                        alt="Data Love Co Logo"
                                        style={{ margin: '0 auto' }}
                                    />
                                    <Typography 
                                        variant="h5" 
                                        color="text.primary" 
                                        sx={{ 
                                            mb: 2, 
                                            fontWeight: 'bold',
                                            textAlign: 'center',
                                            width: '100%'
                                        }}
                                    >
                                        Welcome to Data Love Co
                                    </Typography>
                                </Box>

                                {/* Desktop Hero Section */}
                                <HeroSection sx={{ 
                                    display: { xs: 'none', sm: 'block' },
                                    textAlign: 'center'
                                }}>
                                    <Container maxWidth="lg" sx={{ 
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center'
                                    }}>
                                        <LogoImage 
                                            src="/images/logo_datalove.png" 
                                            alt="Data Love Co Logo"
                                            style={{ margin: '0 auto' }}
                                        />
                                        <Typography 
                                            variant="h4" 
                                            color="text.primary" 
                                            sx={{ 
                                                mb: 2, 
                                                fontWeight: 'bold',
                                                textAlign: 'center',
                                                width: '100%'
                                            }}
                                        >
                                            Welcome to Data Love Co
                                        </Typography>
                                        <Typography 
                                            variant="body1" 
                                            color="text.secondary"
                                            sx={{ 
                                                textAlign: 'center',
                                                maxWidth: '600px'
                                            }}
                                        >
                                            Your comprehensive platform for data analytics, AI-powered insights, and intelligent reporting
                                        </Typography>
                                    </Container>
                                </HeroSection>

                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    alignItems: 'center',
                                    flex: 1,
                                    pb: 6 
                                }}>
                                    <Grid container spacing={3} justifyContent="center" sx={{ maxWidth: '1200px' }}>
                                        {getRoleSpecificActions().map((action, index) => (
                                            <Grid item
                                                xs={12}
                                                sm={6}
                                                md={4}
                                                key={index}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                <ActionCard sx={{ width: '100%', maxWidth: '350px' }}>
                                                    <CardActionArea 
                                                        onClick={action.action} 
                                                        sx={{ 
                                                            height: '100%',
                                                            p: 2
                                                        }}
                                                    >
                                                        <CardContent sx={{ 
                                                            textAlign: 'center', 
                                                            p: 2,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            gap: 1
                                                        }}>
                                                            <CardIcon>
                                                                {action.icon}
                                                            </CardIcon>
                                                            <Typography 
                                                                variant="h6" 
                                                                component="h2" 
                                                                gutterBottom
                                                                sx={{ 
                                                                    fontWeight: 600,
                                                                    mb: 0.5
                                                                }}
                                                            >
                                                                {action.title}
                                                            </Typography>
                                                            <Typography 
                                                                variant="body2" 
                                                                color="text.secondary"
                                                                sx={{ 
                                                                    lineHeight: 1.2
                                                                }}
                                                            >
                                                                {action.description}
                                                            </Typography>
                                                        </CardContent>
                                                    </CardActionArea>
                                                </ActionCard>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            </Container>
                        </Box>
                </Box>
            </Box>
        }

       </React.Fragment>
  );
}
